import {Button, Dropdown, Input, Modal} from "@amzn/alchemy-components-react";
import {useTranslation} from "react-i18next";
import {AlertType, ModalCloseSource, UserType} from "src/common/enums";
import {AlertBar} from "src/components/alert-bar";
import React, {useEffect, useState} from "react";
import {logger} from "src/logger";
import {isEmptyString, isPrimaryAdminRole} from "src/common/functions";
import {isEmailValid} from "src/common/util";
import {useMutation} from "@apollo/client";
import {UpdateUserTypeVariables} from "src/models/update-user-type";
import {UPDATE_USER_TYPE_MUTATION} from "src/common/gql-operations";
import {NETWORK_ONLY} from "src/common/constants";

import initialMetricsPublisher from "src/metrics";
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('modal', 'update-user-type'),
];

//@ ts-ignore
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);


interface UpdateUserTypeModalProps {
    open: boolean;
    setOpen: CallableFunction;
    fullName: string;
    setFullName: CallableFunction;
    tenantId: string;
    setTenantId: CallableFunction;
    principalArn: string;
    setPrincipalArn: CallableFunction;
    scope: string;
    setScope: CallableFunction;
    userType: string;
    setUserType: CallableFunction
}

interface FormState {
    selectedNewRole: string;
    shouldShowExistingAdminRoleDropdown: boolean;
    existingAdminNewRole: string;
    isValid: boolean;
    adminPrimaryEmail: string;
}

interface AlertState {
    type?: AlertType;
    header?: string;
    message?: string;
}

export const UpdateUserTypeModal = (updateUserTypeModalProps: UpdateUserTypeModalProps) => {
    const {t} = useTranslation();

    const [formState, setFormState] = useState<FormState>({
        selectedNewRole: '',
        shouldShowExistingAdminRoleDropdown: false,
        existingAdminNewRole: '',
        isValid: false,
        adminPrimaryEmail: ''
    });

    const [alertState, setAlertState] = useState<AlertState>({
        type: undefined,
        header: undefined,
        message: undefined
    });

    // Track close source
    const [closeSource, setCloseSource] = useState<ModalCloseSource | null>(null);
    const [showingSuccessSummary, setShowingSuccessSummary] = useState(false);


    useEffect(() => {
        // Reset close source when modal opens
        if (updateUserTypeModalProps.open) {
            setCloseSource(null);
        }
    }, [updateUserTypeModalProps.open]);

    useEffect(() => {
        setFormState(prevState => ({ ...prevState, isValid: validateFormState() }));
    }, [formState.selectedNewRole, formState.existingAdminNewRole, formState.adminPrimaryEmail]);

    const handleModalClose = (source: ModalCloseSource) => {
        logger.info(`Modal closing from: ${source}`);
        setCloseSource(source);
        resetForm();
        updateUserTypeModalProps.setOpen(false);
    };

    const validateFormState = (): boolean => {
        // First check if a role is selected
        if (isEmptyString(formState.selectedNewRole)) {
            return false;
        }

        // If it's USER_ADMIN_PRIMARY, need both valid email and admin role
        if (formState.selectedNewRole === UserType.USER_ADMIN_PRIMARY) {
            return !isEmptyString(formState.existingAdminNewRole) &&
                !isEmptyString(formState.adminPrimaryEmail) &&
                isEmailValid(formState.adminPrimaryEmail);
        }

        // If it's ABBM_PRIMARY, only need admin role
        if (formState.selectedNewRole === UserType.ABBM_PRIMARY) {
            return !isEmptyString(formState.existingAdminNewRole);
        }

        // For non-primary roles, just having a role selected is enough
        return true;
    };


    const resetForm = () => {
        setFormState({
            selectedNewRole: '',
            shouldShowExistingAdminRoleDropdown: false,
            existingAdminNewRole: '',
            isValid: false,
            adminPrimaryEmail: ''
        });
        resetAlertBar();
    };

    const resetAlertBar = () => {
        setAlertState({
            type: undefined,
            header: undefined,
            message: undefined
        });
    };

    const showSuccessAlert = (header: string, message: string) => {
        setAlertState({
            type: AlertType.success,
            header,
            message
        });
    };

    const showErrorAlert = (header: string, message: string) => {
        setAlertState({
            type: AlertType.error,
            header,
            message
        });
    };

    const handleInputEmail = (email : string) => {
        try {
            logger.info(`New email is {}, ${email}`);
            setFormState({
                ...formState,
                adminPrimaryEmail: email
            })
        } catch (error) {
            showErrorAlert(
                t('error'),
                t('please-try-again')
            )
        }
    }
    const handleRoleSelection = (newRole: string) => {
        try {
            logger.info(`Selected New Role {}, ${newRole}`);
            setFormState({
                ...formState,
                selectedNewRole: newRole,
                shouldShowExistingAdminRoleDropdown: isPrimaryAdminRole(newRole),
                existingAdminNewRole: '',
                isValid: validateFormState()
            });
        } catch (error) {
            showErrorAlert(
                t('error-selecting-role'),
                t('please-try-again')
            );
        }
    };

    const handleExistingPrimaryAdminNewRoleSelection = (adminNewRole: string) => {
        try {
            logger.info(`Selected Existing Admin New Role {}, ${adminNewRole}`);
            setFormState({
                ...formState,
                existingAdminNewRole: adminNewRole,
                isValid: validateFormState()
            });
        } catch (error) {
            showErrorAlert(
                t('error-selecting-admin-role'),
                t('please-try-again')
            );
        }
    };

    const [updateUserType, { loading }] = useMutation<void, UpdateUserTypeVariables>(
        UPDATE_USER_TYPE_MUTATION,
        {
            fetchPolicy: NETWORK_ONLY,
            onCompleted: () => {
                logger.info("Update User Type succeeded");
                graphqlClientMetricsPublisher.publishCounterMonitor('update-user-type.SUCCESS', 1);
                showSuccessAlert(
                    t('update-user-type-success'),
                    t('user-type-updated-successfully', {
                        fullName: updateUserTypeModalProps.fullName,
                        newRole: formState.selectedNewRole
                    })
                );
                setTimeout(() => {
                    handleModalClose(ModalCloseSource.SUCCESS);
                    window.location.reload();
                }, 5000);
            },
            onError: (error) => {
                logger.error("Update user type failed!", error);
                graphqlClientMetricsPublisher.publishCounterMonitor('update-user-type.FAILURE', 1);
                showErrorAlert(
                    t('update-user-type-failed'),
                    error.message || t('unknown-error')
                );
                setTimeout(() => {
                    handleModalClose(ModalCloseSource.CANCEL);
                    window.location.reload();
                }, 5000);
            }
        }
    );

    const generateSummaryMessage = () => {
        const messages = [];
        messages.push(t('user-role-updated', {
            userName: updateUserTypeModalProps.fullName,
            newRole: formState.selectedNewRole
        }));
        if(formState.selectedNewRole === UserType.USER_ADMIN_PRIMARY || formState.selectedNewRole === UserType.ABBM_PRIMARY){
            const action = formState.existingAdminNewRole === t('delete-existing-admin')
            ? t('has-been-deleted')
                : t('has-been-changed-to', { role: formState.existingAdminNewRole });

            messages.push(t('previous-admin-update',{action}));
        }
        return messages.join(' and ');
    }

    const handleSubmit = async () => {
        try {
            if (!formState.isValid) {
                showErrorAlert(
                    t('invalid-form'),
                    t('please-fill-in-all-fields')
                );
                return;
            }
            if(updateUserTypeModalProps.userType === UserType.ABBM_PRIMARY){
                showErrorAlert(
                    t('cannot-remove-abbm-primary'),
                    t('instructions-to-update-abbm-primary')
                );
                setTimeout(() => {
                    handleModalClose(ModalCloseSource.CANCEL);
                    window.location.reload();
                }, 5000);
                return;
            }
            setShowingSuccessSummary(true);
            graphqlClientMetricsPublisher.publishCounterMonitor('update-user-type.INVOCATION', 1);
            let previousPrimaryRole;
            //if the update is to any existing Primary role.
            if(formState.selectedNewRole === UserType.ABBM_PRIMARY || formState.selectedNewRole === UserType.USER_ADMIN_PRIMARY){
                //if the existing primary needs to be deleted
                if(formState.existingAdminNewRole === t('delete-existing-admin')){
                    previousPrimaryRole= t('delete-existing-admin');
                } else{
                    previousPrimaryRole=formState.existingAdminNewRole;
                }
            } else{
                previousPrimaryRole=undefined;
            }
            await updateUserType({
                variables: {
                    updateUserTypeInput: {
                        tenantId: updateUserTypeModalProps.tenantId,
                        principalArn: updateUserTypeModalProps.principalArn,
                        scope: updateUserTypeModalProps.scope,
                        newRole: formState.selectedNewRole,
                        roleForPrevPrimary: previousPrimaryRole,
                        newUserAdminPrimaryEmail: isEmptyString(formState.adminPrimaryEmail) ? undefined : formState.adminPrimaryEmail
                    }
                }
            });
        } catch (error) {
            showErrorAlert(
                t('update-user-type-failed'),
                error instanceof Error? error.message : t('unknown-error')
            );
        }
    };

    const userTypeOptions = [
        { label: t('select-user-type'), value: '' },
        ...Object.values(UserType)
            .filter(type => type !== UserType.SYSTEM_ADMIN)
            .map(type => ({
            label: type,
            value: type
        }))
    ];

    const getAvailableRolesForExistingAdmin = () => {
        return [
            { label: t('select-role-for-admin'), value: '' },
            { label: t('delete-existing-admin'), value: t('delete-existing-admin')},
            ...userTypeOptions.filter(option =>
                option.value !== UserType.SYSTEM_ADMIN &&
                option.value !== UserType.ABBM_PRIMARY &&
                option.value !== UserType.USER_ADMIN_PRIMARY &&
                option.value !== formState.selectedNewRole &&
                option.value !== '' // Exclude the placeholder
            )
        ];
    };

    const renderModalDescription = () => {
        return (
            <p>
                {t('update-user-type-description', {
                    fullName: updateUserTypeModalProps.fullName,
                    scope: updateUserTypeModalProps.scope
                })}
            </p>
        );
    };

    return (
        <Modal
            id="update-user-type-modal"
            className="mb-1 update-user-type-modal"
            header={t('update-user-type')}
            open={updateUserTypeModalProps.open}
            size="large"
            onClose={(e: React.MouseEvent) => {
                if (e.target === e.currentTarget) {
                    handleModalClose(ModalCloseSource.OVERLAY);
                } else if ((e.target as HTMLElement).closest('.alchemy-modal-close-button')) {
                    handleModalClose(ModalCloseSource.CROSS);
                }
            }}
        >
            <div className="container-fluid" onClick={(e) => e.stopPropagation()}>
                {!showingSuccessSummary ? (
                    <>
                        <div className="row">
                            <div className="col d-inline" id="update-user-type-modal-description">
                                {renderModalDescription()}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col">
                                <Dropdown
                                    id="new-user-type-select"
                                    label={t('select-new-user-type')}
                                    options={userTypeOptions}
                                    onChange={e => handleRoleSelection(e.target.value)}
                                    value={formState.selectedNewRole}
                                    status={!formState.selectedNewRole ? t('required') : ''}
                                />
                            </div>
                        </div>

                        {formState.selectedNewRole && (
                            <div className="role-change-text mt-2">
                                <p className="text-secondary">
                                    {t('role-change-description', {
                                        fullName: updateUserTypeModalProps.fullName,
                                        newRole: formState.selectedNewRole,
                                        scope: updateUserTypeModalProps.scope
                                    })}
                                </p>
                            </div>
                        )}

                        {formState.selectedNewRole === UserType.USER_ADMIN_PRIMARY && (
                            <div className="row mb-3">
                                <div className="col">
                                    <Input id="admin-email-input"
                                           label={t('email')}
                                           type="email"
                                           value={formState.adminPrimaryEmail}
                                           onInput={e => {
                                              handleInputEmail(e.target.value)
                                           }}
                                           status={!isEmailValid(formState.adminPrimaryEmail) ? t('required') : ''}
                                    />
                                </div>
                            </div>
                        )}

                        {formState.shouldShowExistingAdminRoleDropdown && (
                            <div className="row mb-3">
                                <div className="col">
                                    <Dropdown
                                        id="existing-admin-new-role-select"
                                        label={t('select-role-for-current-admin',{
                                            role: formState.selectedNewRole
                                        })}
                                        options={getAvailableRolesForExistingAdmin()}
                                        onChange={e => handleExistingPrimaryAdminNewRoleSelection(e.target.value)}
                                        value={formState.existingAdminNewRole}
                                        helpText={t('current-admin-role-required',{
                                            role: formState.selectedNewRole
                                        })}
                                        status={!formState.existingAdminNewRole ? t('required') : ''}
                                    />
                                    {formState.existingAdminNewRole === t('delete-existing-admin') && (
                                        <div className="delete-warning-message">
                                            {t('delete-existing-admin-warning')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="row d-flex justify-content-center">
                            <Button
                                id="update-user-type-modal-cancel-button"
                                size="lg"
                                label={t('cancel')}
                                onClick={() => handleModalClose(ModalCloseSource.CANCEL)}
                                className="mx-1"
                            />
                            <Button
                                id="update-user-type-modal-update-button"
                                size="lg"
                                label={t('confirm')}
                                variant="action"
                                disabled={!formState.isValid}
                                onClick={handleSubmit}
                                className="mx-1"
                            />
                        </div>
                </>
                ) : (
                    <div className="update-message">
                        {generateSummaryMessage()}
                    </div>
                )}
            </div>
            {alertState.message && (
                <AlertBar
                    id="update-user-type-modal-alert-bar"
                    result={alertState.type!}
                    dismissible={AlertType.error !== alertState.type}
                    header={alertState.header!}
                    message={alertState.message}
                    reset={resetAlertBar}
                />
            )}
        </Modal>
    );
};
